<template>
    <div
        class="stock-cell"
        :class="{ 'is-editing': editing, 'is-editable': editable, 'is-dirty': isDirty }"
        @click.stop="onCellClick"
    >
        <div v-if="editing" class="stock-cell__editor">
            <StockStepper
                ref="stepper"
                :value="currentDraft"
                :autofocus="!bulk"
                :commitOnBlur="!bulk"
                :trapTab="bulk"
                @input="$emit('input', $event)"
                @navigate="$emit('navigate', $event)"
                @commit="$emit('commit', $event)"
                @cancel="$emit('cancel')"
            />
            <span v-if="variation" class="stock-cell__variation" :class="variationClass" :title="variationTooltip">
                {{ variation }}
            </span>
        </div>
        <template v-else>
            <span
                class="stock-cell__value"
                :class="[isDirty ? 'poppinsSemiBold' : 'poppinsRegular', `tone-${tone.key}`]"
                :style="{ color: tone.color }"
                :title="isInfinite ? $t('stock.infinite') : null"
            >
                {{ display }}
            </span>
            <span v-if="isDirty && variation" class="stock-cell__variation stock-cell__variation--static" :class="variationClass">
                {{ variation }}
            </span>
            <button
                v-if="editable"
                type="button"
                class="stock-cell__pencil"
                :title="$t('stock.edit')"
                :aria-label="$t('stock.edit')"
                @click.stop="$emit('edit')"
            >
                <SquarePen :size="14" />
            </button>
        </template>
    </div>
</template>

<script>
import { SquarePen } from 'lucide-vue'
import StockStepper from './StockStepper.vue'
import { stockTone, formatStock, variationLabel, variationTitle } from './stockUtils'

export default {
    name: 'StockCell',
    components: { SquarePen, StockStepper },
    props: {
        /** Valeur enregistrée (null = ∞). */
        value: {
            type: Number,
            default: null,
        },
        /** Brouillon en cours (undefined = aucun brouillon). */
        draft: {
            type: Number,
            default: undefined,
        },
        editable: {
            type: Boolean,
            default: false,
        },
        editing: {
            type: Boolean,
            default: false,
        },
        /** Mode « Gérer les stocks » : toutes les lignes en édition, le blur n'enregistre rien. */
        bulk: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        hasDraft() {
            return this.draft !== undefined
        },
        currentDraft() {
            return this.hasDraft ? this.draft : this.value
        },
        isDirty() {
            return this.hasDraft && this.draft !== this.value
        },
        isInfinite() {
            return this.currentDraft === null || this.currentDraft === undefined
        },
        tone() {
            return stockTone(this.currentDraft)
        },
        display() {
            return formatStock(this.currentDraft)
        },
        variation() {
            return variationLabel(this.value, this.currentDraft)
        },
        variationTooltip() {
            return variationTitle(this.value, this.currentDraft)
        },
        variationClass() {
            const from = this.value
            const to = this.currentDraft
            if (from === null || to === null || from === undefined || to === undefined) return 'is-neutral'
            return to > from ? 'is-up' : 'is-down'
        },
    },
    methods: {
        onCellClick() {
            if (this.editable && !this.editing) this.$emit('edit')
        },
    },
}
</script>

<style scoped lang="scss">
.stock-cell {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 32px;
    padding: 0 22px;

    &.is-editable {
        cursor: pointer;
    }

    &.is-editing {
        cursor: default;
    }
}

.stock-cell__value {
    font-size: 14px;
    font-variant-numeric: tabular-nums;
    line-height: 1;
}

.stock-cell__pencil {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #7d2dbd;
    opacity: 0;
    transition:
        opacity 0.15s ease,
        background-color 0.15s ease;

    &:hover,
    &:focus-visible {
        opacity: 1;
        background: #f3eafa;
        outline: none;
    }
}

.stock-cell__editor {
    position: relative;
    display: inline-flex;
    align-items: center;
}

.stock-cell__variation {
    font-size: 12px;
    font-family: 'Poppins Medium', 'Poppins', sans-serif;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: #4b5563;

    &.is-up {
        color: #15803d;
    }

    &.is-down {
        color: #911414;
    }

    &.is-neutral {
        color: #7d2dbd;
    }
}

.stock-cell__editor .stock-cell__variation {
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 8px;
}

@media (max-width: 767px) {
    .stock-cell__editor .stock-cell__variation {
        position: static;
        transform: none;
        margin-left: 8px;
    }
}
</style>

<!-- Non scopé : le crayon ne se révèle qu'au survol de la ligne du tableau hôte -->
<style lang="scss">
tr:hover > td .stock-cell__pencil,
.stock-cell.is-dirty .stock-cell__pencil {
    opacity: 1;
}

@media (hover: none) {
    .stock-cell__pencil {
        opacity: 0.6;
    }
}
</style>
